import React, { useEffect, useContext, useCallback } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import { getEntityDetail, getEntityId } from "us.common/functions";
import { INavigationData } from "us.collection/interfaces";
import {
  $Skeleton,
  $Divider,
  $Button,
  $Tooltip,
  ActivityExecution,
  $AmountLabel,
  $DateLabel,
} from "us.common/components";
import * as Actions from "us.collection.transactions/actions";
import { BoxIcons, IconTypes, InfoCircleFilled } from "us.icons";
import {
  PendingActivityExecution,
  AddToFollowUpButton,
} from "us.collection/components";
import "./Header.scss";
import { ConfigurationContext } from "us.common/ConfigurationContext/ConfigurationProvider";
import {
  EntityType,
  EntityTypeShortForm,
  ExecutionSource,
  Mode,
} from "us.common/constants";
import {
  getSelectedCaseId,
  getSelectedCaseType,
  isDisabledActivityExecutionForCaseAndSubCase,
  isEnabledDefaultActivityExecution,
} from "./Functions";
import { RootState } from "us.helper/types";

const Header: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const context = useContext(ConfigurationContext);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    arDetails,
    isArDetailsFetching,
    metaData,
    selectedCaseAndSubCase,
    isFollowUpDrawerVisible,
    isAssetDrawerVisible,
    getArDetails,
  } = props;

  const { isAddressKnown } = metaData.data ?? {};
  const state = location.state as INavigationData;
  const { refreshCount = 0, currentTab, arId } = state ?? {};
  const pathname = location.pathname;
  const entityDetail = getEntityDetail(pathname, metaData.data);
  const { entityId } = entityDetail;
  const { data, filterType } = selectedCaseAndSubCase;

  useEffect(() => {
    if (state && currentTab === "ar" && !isArDetailsFetching)
      if (arId) {
        getArDetails(arId);
      }
  }, [arId, refreshCount]);

  /**
   * Description - Check whether the given item should be available or not in the header
   * @param {string} item - Header item name
   */
  const isHeaderItemAvailable = useCallback(
    (item: string) => {
      if (context && context.componentPermission["ar"]) {
        return context.componentPermission["ar"]?.components[item]?.isEnabled;
      } else {
        return false;
      }
    },
    [context]
  );

  /**
   * @function
   * @description handle navigation after execute activity
   */
  const navigateAfterExecution = () => {
    navigate({ ...location, pathname: `/ar/${entityId}/history` }, { state });
  };

  return (
    <div className="dom-ar-header">
      <$Skeleton
        loading={isArDetailsFetching}
        active
        paragraph={{ rows: 0, width: 100 }}
      >
        <div className="d-flex flex-wrap">
          <div className="d-flex my-1">
            <span className="bui-label mr-2 text-nowrap">
              {t("US.COLLECTION.LAYOUT:AR.AR_NO")}
            </span>
            <strong className="text-truncate AR_NO">
              {arDetails?.arId ? arDetails?.arId : "--"}
            </strong>
            <$Divider type="vertical" className="bui-devider" />
          </div>

          <div className="d-flex my-1">
            <span className="bui-label mr-2 text-nowrap">
              {t("US.COLLECTION.LAYOUT:AR.CREDITOR")}
            </span>
            <$Tooltip
              placement="topLeft"
              title={
                <>
                  {arDetails?.pid ? <span>{arDetails?.pid}</span> : "---"}
                  {arDetails?.pid && arDetails?.creditorName ? (
                    <span className="mx-1">-</span>
                  ) : (
                    ""
                  )}
                  {arDetails?.creditorName ? (
                    <span>{arDetails?.creditorName}</span>
                  ) : (
                    "---"
                  )}
                </>
              }
            >
              <strong className="text-truncate arh-truncate">
                {arDetails?.pid ? <span>{arDetails?.pid}</span> : "---"}
                {arDetails?.pid && arDetails?.creditorName ? (
                  <span className="mx-1">-</span>
                ) : (
                  ""
                )}
                {arDetails?.creditorName ? (
                  <span>{arDetails?.creditorName}</span>
                ) : (
                  "---"
                )}
              </strong>
            </$Tooltip>

            <$Divider type="vertical" className="bui-devider" />
          </div>

          <div className="d-flex my-1">
            <$Tooltip
              placement="topLeft"
              title={
                isAddressKnown ? (
                  <>
                    {arDetails?.cid ? <span>{arDetails?.cid}</span> : "---"}
                    {arDetails?.cid && arDetails?.debtorName ? (
                      <span className="mx-1">-</span>
                    ) : (
                      ""
                    )}
                    {arDetails?.debtorName ? (
                      <span>{arDetails?.debtorName}</span>
                    ) : (
                      "---"
                    )}
                  </>
                ) : (
                  t(`US.COMMON:ACTIVITY_EXECUTION.ADDRESS_UNKNOWN`)
                )
              }
            >
              {!isAddressKnown && (
                <span className="text-warning mr-2">
                  <InfoCircleFilled />
                </span>
              )}

              <span className="bui-label mr-2 text-nowrap">
                {t("US.COLLECTION.LAYOUT:AR.DEBTOR")}
              </span>
              <strong className="text-truncate arh-truncate">
                {arDetails?.cid ? <span>{arDetails?.cid}</span> : "---"}
                {arDetails?.cid && arDetails?.debtorName ? (
                  <span className="mx-1">-</span>
                ) : (
                  ""
                )}
                {arDetails?.debtorName ? (
                  <span>{arDetails?.debtorName}</span>
                ) : (
                  "---"
                )}
              </strong>
            </$Tooltip>
            <$Divider type="vertical" className="bui-devider" />
          </div>

          <div className="d-flex my-1">
            <span className="bui-label mr-2 text-nowrap">
              {t("US.COLLECTION.LAYOUT:AR.LAST_PAYMENT")}
            </span>
            <strong className="text-truncate LAST_PAYMENT">
              {arDetails?.lastPaymentDate ? (
                <$DateLabel value={arDetails?.lastPaymentDate} />
              ) : (
                "--"
              )}
            </strong>
            <$Divider type="vertical" className="bui-devider" />
          </div>

          <div className="d-flex my-1">
            <span className="bui-label mr-2 text-nowrap">
              {t("US.COLLECTION.LAYOUT:AR.TOTAL_BALANCE")}
            </span>
            <strong className="text-truncate TOTAL_BALANCE">
              {arDetails?.balance != null ||
              arDetails?.balance != "undefined" ? (
                <$AmountLabel value={arDetails?.balance} />
              ) : (
                "--"
              )}
            </strong>
            <$Divider type="vertical" className="bui-devider" />
          </div>

          <div className="d-flex my-1">
            <$Button size="small" onClick={() => getArDetails(arId, true)}>
              <BoxIcons
                type={IconTypes.BOX_ICON}
                name="update-balance"
                className="mr-2"
              />
              <span>{t("US.COLLECTION.LAYOUT:AR.UPDATE_BALANCE")}</span>
            </$Button>
          </div>
        </div>
        <div className="d-flex align-items-center mt-2">
          {isEnabledDefaultActivityExecution(pathname) && (
            <ActivityExecution
              mode={Mode.Initial}
              isHideTable={false}
              isDisabled={isFollowUpDrawerVisible || isAssetDrawerVisible}
              isBulkExecutionEnabled={false}
              isGroupActivityOptions={false}
              isActivityOptionWithCode={true}
              isHideDatePicker={false}
              activityAPIRequest={{
                endPoint: `activities`,
                parameters: {
                  entitytype: EntityTypeShortForm.AR,
                  entitystate: "",
                  canExecute: true,
                },
              }}
              executeActivityEndPoint="activities"
              groupingProperty={{
                groupingLabel: "activityGroup",
                groupingValue: "activityGroupId",
                tableGrouping: "type",
              }}
              entityId={getEntityId(entityDetail)}
              entityType={EntityType.AR}
              isPopConfirmEnabled={true}
              propConfirmDetail={{
                title: "ARE_YOU_SURE_YOU_WANT_TO_EXECUTE_THIS_ACTIVITY",
              }}
              navigateAfterExecution={navigateAfterExecution}
              isPopConfirmEnabledDropDown={false}
              isAddressUnknown={!isAddressKnown}
            />
          )}

          {!isEnabledDefaultActivityExecution(pathname) && (
            <ActivityExecution
              mode={Mode.Click}
              isHideTable={false}
              isDisabled={
                isDisabledActivityExecutionForCaseAndSubCase(
                  data,
                  filterType
                ) ||
                isFollowUpDrawerVisible ||
                isAssetDrawerVisible
              }
              isBulkExecutionEnabled={true}
              isGroupActivityOptions={false}
              isActivityOptionWithCode={true}
              isHideDatePicker={false}
              activityAPIRequest={{
                endPoint: `activities`,
                parameters: {
                  entitytype: getSelectedCaseType(data)[0],
                  entitystate: "All",
                  canExecute: true,
                },
              }}
              executeActivityEndPoint="bulkactivity"
              groupingProperty={{
                groupingLabel: "activityGroup",
                groupingValue: "activityGroupId",
                tableGrouping: "type",
              }}
              isPopConfirmEnabled={true}
              isPopConfirmEnabledDropDown={true}
              propConfirmDetail={{
                title: "ARE_YOU_SURE_YOU_WANT_TO_EXECUTE_THIS_ACTIVITY",
              }}
              selectedEntities={getSelectedCaseId(data)}
              maxEntityCountExecuteAtOnce={100}
              isRowKeysAsEntityIds={true}
              bulkExecutionSource={
                ExecutionSource.CasesAndSubCasesBulkExecution
              }
              isAddressUnknown={!isAddressKnown}
            />
          )}

          <div className="ml-auto d-flex">
            {isHeaderItemAvailable("addToFollowUp") && <AddToFollowUpButton />}
            <PendingActivityExecution {...props} />
          </div>
        </div>
      </$Skeleton>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const {
    common,
    transaction,
    domainView,
    casesAndSubCases,
    entityFollowUps,
    assets,
  } = state;
  const { currentLanguage, currentDateFormat, currentCurrency } = common;
  const { arDetails, isArDetailsFetching } = transaction;
  const { metaData } = domainView;
  const { selectedCaseAndSubCase } = casesAndSubCases;
  const { drawer } = entityFollowUps;
  const { visible: isFollowUpDrawerVisible } = drawer;
  
  return {
    currentLanguage,
    currentCurrency,
    currentDateFormat,
    arDetails,
    isArDetailsFetching,
    metaData,
    selectedCaseAndSubCase,
    isFollowUpDrawerVisible,
    isAssetDrawerVisible: assets.drawerInfo.visible,
  };
};

const mapDispatchToProps = {
  getArDetails: Actions.transactions.getArDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(Header);
